import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Flex,
  TableCell,
  TableRow,
  Text,
} from '@optiaxiom/react';
import {
  IconAngleDown,
  IconAngleRight,
  IconEllipsisSolid,
  IconPen,
  IconTrashCan,
} from '@optimizely/axiom-icons';

import { AuthProvider } from '@frontend/types';

import { useDeleteAuthProvider } from '../../hooks';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: FIXME: OPAL-2388 - Missing types file
import type { ScopeBundle } from '../../types';

import { ScopeBundleTable } from './ScopeBundleTable';

type AuthProviderRowProps = {
  provider: AuthProvider;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onAddBundle: () => void;
  onEditBundle: (bundle: ScopeBundle) => void;
  onEditProvider: () => void;
};

const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  } catch {
    return dateString;
  }
};

export function AuthProviderRow({
  provider,
  isExpanded,
  onToggleExpand,
  onAddBundle,
  onEditBundle,
  onEditProvider,
}: AuthProviderRowProps) {
  const { mutate: deleteProvider, isPending: isDeleting } = useDeleteAuthProvider();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const bundleCount = provider.scope_bundles?.length ?? 0;

  const handleDelete = () => {
    deleteProvider(provider.id, {
      onSuccess: () => {
        setIsDeleteDialogOpen(false);
      },
    });
  };

  return (
    <>
      <TableRow>
        <TableCell onClick={onToggleExpand} style={{ cursor: 'pointer' }}>
          {isExpanded ? <IconAngleDown size={16} /> : <IconAngleRight size={16} />}
        </TableCell>
        <TableCell>
          <Flex flexDirection="column">
            <Text fontWeight="500">{provider.name}</Text>
            {provider.description && (
              <Text color="fg.tertiary" fontSize="sm">
                {provider.description}
              </Text>
            )}
          </Flex>
        </TableCell>
        <TableCell>
          <Badge intent="neutral">{provider.auth_type}</Badge>
        </TableCell>
        <TableCell>
          <Badge intent={bundleCount > 0 ? 'primary' : 'neutral'}>
            {bundleCount} {bundleCount === 1 ? 'bundle' : 'bundles'}
          </Badge>
        </TableCell>
        <TableCell>{formatDate(provider.created_at)}</TableCell>
        <TableCell>
          <DropdownMenu>
            <DropdownMenuTrigger
              appearance="subtle"
              aria-label="Actions"
              icon={<IconEllipsisSolid />}
            />
            <DropdownMenuContent side="left">
              <DropdownMenuItem onClick={onEditProvider}>
                <IconPen size={16} />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setIsDeleteDialogOpen(true)} disabled={isDeleting}>
                <IconTrashCan size={16} />
                {isDeleting ? 'Deleting...' : 'Delete'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>Delete Auth Provider?</AlertDialogHeader>
              <AlertDialogBody>
                Are you sure you want to delete &quot;{provider.name}&quot;? This will also delete
                all associated scope bundles. This action cannot be undone.
              </AlertDialogBody>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
                  {isDeleting ? 'Deleting...' : 'Yes, delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TableCell>
      </TableRow>

      {isExpanded && (
        <TableRow>
          <TableCell colSpan={6} p="0">
            <Flex bg="bg.secondary" p="16">
              <ScopeBundleTable
                bundles={provider.scope_bundles ?? []}
                onAddBundle={onAddBundle}
                onEditBundle={onEditBundle}
              />
            </Flex>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
